import {
  ALL_DYNAMIC_COLUMNS_QUERY,
  DynamicTable,
} from '@/modules/dynamic_tables'
import type {
  AllDynamicColumnsQuery,
  AllDynamicColumnsQueryVariables,
} from '@/modules/dynamic_tables'
import { Module } from '@/modules/companies/consts'
import { useQuery } from '@apollo/client'
import { useCallback, useMemo } from 'react'
import { ALL_ISSUE_RELATIONS_QUERY } from '../queries'
import { enabledColumnNames, visibleRelationColumns } from './columns'
import type { IssueTableColumnValue, IssueTableRelationColumn } from './types'

interface RelationNode {
  id: string
  name: string
  visibleInTable: boolean
}

interface AllIssueRelationsData {
  allIssueRelations: { edges: { node: RelationNode }[] }
}

export interface UseIssueTableColumns {
  loading: boolean
  /** Built-in columns the company still shows. */
  isVisible: (column: IssueTableColumnValue) => boolean
  /** Company-defined relations shown as their own columns, in name order. */
  relationColumns: IssueTableRelationColumn[]
}

/**
 * Which columns the issue table renders.
 *
 * Two sources on purpose: the built-in columns are DynamicColumn rows, the
 * same mechanism the activities and salary tables use, while relation columns
 * come from the relations themselves — a DynamicColumn keys on a static name,
 * so a row per company-defined relation would need syncing on every rename and
 * delete.
 */
export function useIssueTableColumns(): UseIssueTableColumns {
  const { data: columnData, loading: columnsLoading } = useQuery<
    AllDynamicColumnsQuery,
    AllDynamicColumnsQueryVariables
  >(ALL_DYNAMIC_COLUMNS_QUERY, {
    variables: { moduleName: Module.ISSUES, table: DynamicTable.ISSUES },
    fetchPolicy: 'cache-first',
  })

  const { data: relationData, loading: relationsLoading } =
    useQuery<AllIssueRelationsData>(ALL_ISSUE_RELATIONS_QUERY, {
      fetchPolicy: 'cache-first',
    })

  const enabled = useMemo(
    () => enabledColumnNames(columnData?.allDynamicColumns.edges ?? []),
    [columnData]
  )

  const hasColumnRows = (columnData?.allDynamicColumns.edges.length ?? 0) > 0

  const isVisible = useCallback(
    (column: IssueTableColumnValue) => {
      // Before the rows load — and for a company whose seed has not run yet —
      // show everything. Hiding on a miss would blank the table on every cold
      // load.
      if (!hasColumnRows) return true
      return enabled.has(column)
    },
    [enabled, hasColumnRows]
  )

  const relationColumns = useMemo(
    () => visibleRelationColumns(relationData?.allIssueRelations.edges ?? []),
    [relationData]
  )

  return {
    loading: columnsLoading || relationsLoading,
    isVisible,
    relationColumns,
  }
}
